import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  getSlackUsers,
  reportTechBug,
  type Ticket,
} from "@/lib/api";
import { Loader2, X, Check, Upload, FileImage, Film } from "lucide-react";
import { cn } from "@/lib/utils";

// Product options matching Slack /bug form
const PRODUCTS = [
  "Atlas Aggregator",
  "Atlas Online",
  "Atlas POS",
  "Atlas Smart Logistics",
  "Atlas Scan to Order",
  "Atlas KDS",
  "Atlas Merchant Portal",
  "Atlas Connect",
  "Atlas Kiosk",
  "Atlas Dispatch",
] as const;

interface ReportTechBugModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticket: Ticket;
  onSuccess: (threadUrl: string) => void;
}

export function ReportTechBugModal({
  open,
  onOpenChange,
  ticket,
  onSuccess,
}: ReportTechBugModalProps) {
  const queryClient = useQueryClient();

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [merchantOutlet, setMerchantOutlet] = useState("");
  const [affectedOrders, setAffectedOrders] = useState("");
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]);
  const [ownerSearch, setOwnerSearch] = useState("");
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const ownerDropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        ownerDropdownRef.current &&
        !ownerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowOwnerDropdown(false);
      }
    }

    if (showOwnerDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showOwnerDropdown]);

  // Initialize form with ticket data (including AI-extracted fields)
  useEffect(() => {
    if (open) {
      // Auto-fill date with current date (YYYY-MM-DD format)
      const now = new Date();
      setDate(now.toISOString().split("T")[0]);
      // Leave time empty per requirements
      setTime("");

      // Pre-fill merchant/outlet: prefer extracted outlet name, fallback to merchant name
      setMerchantOutlet(
        ticket.extracted_outlet_name || ticket.merchant_name || ""
      );

      // Pre-fill affected orders from extracted order IDs
      if (ticket.extracted_order_ids) {
        try {
          const orderIds = JSON.parse(ticket.extracted_order_ids);
          setAffectedOrders(Array.isArray(orderIds) ? orderIds.join(", ") : "");
        } catch {
          setAffectedOrders("");
        }
      } else {
        setAffectedOrders("");
      }

      // Pre-fill bug description with extracted issue description
      setDescription(ticket.extracted_issue_description || "");

      // Reset fields that can't be pre-filled
      setTitle("");
      setSelectedProducts([]);
      setSelectedOwners([]);
      setOwnerSearch("");
      setSelectedFiles([]);
    }
  }, [open, ticket]);

  // Fetch Slack users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ["slack-users"],
    queryFn: getSlackUsers,
    enabled: open,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const users = usersData?.users || [];

  // Filter users based on search
  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(ownerSearch.toLowerCase()) ||
      user.realName?.toLowerCase().includes(ownerSearch.toLowerCase()) ||
      user.email?.toLowerCase().includes(ownerSearch.toLowerCase())
  );

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: () =>
      reportTechBug(ticket.id, {
        title,
        description,
        products: selectedProducts,
        date,
        time,
        merchantOutlet,
        affectedOrders: affectedOrders || undefined,
        productOwners: selectedOwners,
        files: selectedFiles.length > 0 ? selectedFiles : undefined,
      }),
    onSuccess: (data) => {
      // Invalidate ticket queries to refresh the slack_bug_thread_url
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      queryClient.invalidateQueries({ queryKey: ["ticket", ticket.id] });
      onSuccess(data.threadUrl);
      onOpenChange(false);
    },
  });

  // Toggle product selection
  const toggleProduct = (product: string) => {
    setSelectedProducts((prev) =>
      prev.includes(product)
        ? prev.filter((p) => p !== product)
        : [...prev, product]
    );
  };

  // Toggle owner selection
  const toggleOwner = (userId: string) => {
    setSelectedOwners((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Remove a file from selection
  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Validation
  const isValid =
    title.trim() &&
    description.trim() &&
    selectedProducts.length > 0 &&
    selectedOwners.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Report tech bug</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="time">Time</Label>
              <Input
                id="time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                placeholder="HH:MM"
              />
            </div>
          </div>

          {/* Products (multi-select) */}
          <div className="space-y-2">
            <Label>Product(s) *</Label>
            <div className="grid grid-cols-2 gap-2">
              {PRODUCTS.map((product) => (
                <label
                  key={product}
                  className={cn(
                    "flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted/50",
                    selectedProducts.includes(product) && "bg-primary/10 border-primary"
                  )}
                >
                  <Checkbox
                    checked={selectedProducts.includes(product)}
                    onCheckedChange={() => toggleProduct(product)}
                  />
                  <span className="text-sm">{product}</span>
                </label>
              ))}
            </div>
            {selectedProducts.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Select at least one product
              </p>
            )}
          </div>

          {/* Product owners / engineers (multi-select from Slack users) */}
          <div className="space-y-2">
            <Label>Product owners / engineers *</Label>

            {/* Selected owners display */}
            {selectedOwners.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedOwners.map((id) => {
                  const user = users.find((u) => u.id === id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 rounded text-sm"
                    >
                      {user?.name || id}
                      <button
                        type="button"
                        onClick={() => toggleOwner(id)}
                        className="hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Search input */}
            <div className="relative" ref={ownerDropdownRef}>
              <Input
                value={ownerSearch}
                onChange={(e) => {
                  setOwnerSearch(e.target.value);
                  setShowOwnerDropdown(true);
                }}
                onFocus={() => setShowOwnerDropdown(true)}
                placeholder="Search for team members..."
              />

              {/* Dropdown */}
              {showOwnerDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {usersLoading ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      <p className="text-sm text-muted-foreground mt-1">
                        Loading users...
                      </p>
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No users found
                    </div>
                  ) : (
                    <>
                      {filteredUsers.slice(0, 20).map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          className={cn(
                            "w-full px-3 py-2 text-left hover:bg-muted flex items-center justify-between",
                            selectedOwners.includes(user.id) && "bg-primary/10"
                          )}
                          onClick={() => {
                            toggleOwner(user.id);
                            setOwnerSearch("");
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {user.avatar && (
                              <img
                                src={user.avatar}
                                alt=""
                                className="w-6 h-6 rounded"
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium">{user.name}</div>
                              {user.email && (
                                <div className="text-xs text-muted-foreground">
                                  {user.email}
                                </div>
                              )}
                            </div>
                          </div>
                          {selectedOwners.includes(user.id) && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </button>
                      ))}
                      {filteredUsers.length > 20 && (
                        <div className="p-2 text-center text-xs text-muted-foreground">
                          Showing first 20 results. Refine your search.
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {selectedOwners.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Select at least one person to notify
              </p>
            )}
          </div>

          {/* Bug title */}
          <div className="space-y-2">
            <Label htmlFor="title">Bug title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of the bug"
            />
          </div>

          {/* Bug description */}
          <div className="space-y-2">
            <Label htmlFor="description">Bug description *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detailed description of the issue..."
              rows={4}
            />
          </div>

          {/* Merchant / outlet */}
          <div className="space-y-2">
            <Label htmlFor="merchant">Merchant / outlet</Label>
            <Input
              id="merchant"
              value={merchantOutlet}
              onChange={(e) => setMerchantOutlet(e.target.value)}
              placeholder="Affected merchant or outlet name"
            />
          </div>

          {/* Affected orders */}
          <div className="space-y-2">
            <Label htmlFor="orders">Affected orders (IDs)</Label>
            <Input
              id="orders"
              value={affectedOrders}
              onChange={(e) => setAffectedOrders(e.target.value)}
              placeholder="e.g., ORD-123, ORD-456"
            />
          </div>

          {/* Screenshots / recordings */}
          <div className="space-y-2">
            <Label>Screenshots / recordings</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors",
                "hover:border-primary/50 hover:bg-muted/50"
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Click to upload images or videos
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG, GIF, MP4, MOV up to 10MB each
              </p>
            </div>

            {/* Selected files preview */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2 mt-3">
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-3 p-2 bg-muted/50 rounded border"
                  >
                    {file.type.startsWith("image/") ? (
                      <FileImage className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    ) : (
                      <Film className="h-5 w-5 text-purple-500 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="p-1 hover:bg-muted rounded"
                    >
                      <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Extracted screenshots from conversation */}
            {ticket.extracted_screenshots && (() => {
              try {
                const urls = JSON.parse(ticket.extracted_screenshots);
                if (Array.isArray(urls) && urls.length > 0) {
                  return (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-muted-foreground mb-2">
                        Screenshots from conversation:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {urls.map((url: string, i: number) => (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block border rounded overflow-hidden hover:ring-2 hover:ring-primary"
                          >
                            <img
                              src={url}
                              alt={`Screenshot ${i + 1}`}
                              className="w-20 h-20 object-cover"
                            />
                          </a>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Click to open. Download and re-upload if needed.
                      </p>
                    </div>
                  );
                }
              } catch {
                // Invalid JSON, ignore
              }
              return null;
            })()}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => submitMutation.mutate()}
            disabled={!isValid || submitMutation.isPending}
          >
            {submitMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Posting...
              </>
            ) : (
              "Report bug"
            )}
          </Button>
        </DialogFooter>

        {submitMutation.isError && (
          <p className="text-sm text-destructive text-center">
            {submitMutation.error instanceof Error
              ? submitMutation.error.message
              : "Failed to report bug"}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
